import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface PomodoroSession {
  id: string
  type: 'work' | 'shortBreak' | 'longBreak'
  duration: number
  subject?: string
  topic?: string
  startTime: string
  endTime?: string
  completed: boolean
  interrupted: boolean
}

interface PomodoroSettings {
  workDuration: number // in minutes
  shortBreakDuration: number
  longBreakDuration: number
  longBreakInterval: number // after how many work sessions
  autoStartBreaks: boolean
  autoStartWork: boolean
  soundEnabled: boolean
  notificationsEnabled: boolean
  tickingSound: boolean
}

interface PomodoroStats {
  totalSessions: number
  completedSessions: number
  totalWorkTime: number
  totalBreakTime: number
  streakDays: number
  todaySessions: number
  weekSessions: number
  averageSessionLength: number
  subjectTime: Record<string, number>
}

interface PomodoroState {
  currentSession: PomodoroSession | null
  isActive: boolean
  isPaused: boolean
  timeRemaining: number
  currentCycle: number
  sessionsUntilLongBreak: number
  sessions: PomodoroSession[]
  settings: PomodoroSettings
  stats: PomodoroStats
  selectedSubject: string
  selectedTopic: string
  customTimer: {
    isActive: boolean
    duration: number
    timeRemaining: number
    title: string
  }
}

const initialState: PomodoroState = {
  currentSession: null,
  isActive: false,
  isPaused: false,
  timeRemaining: 25 * 60, // 25 minutes in seconds
  currentCycle: 0,
  sessionsUntilLongBreak: 4,
  sessions: [
    {
      id: '1',
      type: 'work',
      duration: 25 * 60,
      subject: 'Matematik',
      topic: 'Cebirsel İfadeler',
      startTime: '2024-01-15T09:00:00Z',
      endTime: '2024-01-15T09:25:00Z',
      completed: true,
      interrupted: false
    },
    {
      id: '2',
      type: 'shortBreak',
      duration: 5 * 60,
      startTime: '2024-01-15T09:25:00Z',
      endTime: '2024-01-15T09:30:00Z',
      completed: true,
      interrupted: false
    },
    {
      id: '3',
      type: 'work',
      duration: 25 * 60,
      subject: 'Türkçe',
      topic: 'Okuduğunu Anlama',
      startTime: '2024-01-15T09:30:00Z',
      endTime: '2024-01-15T09:55:00Z',
      completed: true,
      interrupted: false
    }
  ],
  settings: {
    workDuration: 25,
    shortBreakDuration: 5,
    longBreakDuration: 15,
    longBreakInterval: 4,
    autoStartBreaks: false,
    autoStartWork: false,
    soundEnabled: true,
    notificationsEnabled: true,
    tickingSound: false
  },
  stats: {
    totalSessions: 15,
    completedSessions: 13,
    totalWorkTime: 325 * 60, // in seconds
    totalBreakTime: 65 * 60,
    streakDays: 5,
    todaySessions: 3,
    weekSessions: 15,
    averageSessionLength: 24.5 * 60,
    subjectTime: {
      'Matematik': 150 * 60,
      'Türkçe': 100 * 60,
      'Fen Bilimleri': 75 * 60
    }
  },
  selectedSubject: '',
  selectedTopic: '',
  customTimer: {
    isActive: false,
    duration: 30 * 60,
    timeRemaining: 30 * 60,
    title: 'Özel Zamanlayıcı'
  }
}

const pomodoroSlice = createSlice({
  name: 'pomodoro',
  initialState,
  reducers: {
    startSession: (state, action: PayloadAction<{ type: 'work' | 'shortBreak' | 'longBreak'; subject?: string; topic?: string }>) => {
      const duration = action.payload.type === 'work' 
        ? state.settings.workDuration * 60
        : action.payload.type === 'shortBreak'
        ? state.settings.shortBreakDuration * 60
        : state.settings.longBreakDuration * 60

      const newSession: PomodoroSession = {
        id: Date.now().toString(),
        type: action.payload.type,
        duration,
        subject: action.payload.subject,
        topic: action.payload.topic,
        startTime: new Date().toISOString(),
        completed: false,
        interrupted: false
      }

      state.currentSession = newSession
      state.timeRemaining = duration
      state.isActive = true
      state.isPaused = false

      if (action.payload.type === 'work') {
        state.currentCycle += 1
        state.sessionsUntilLongBreak -= 1
        if (state.sessionsUntilLongBreak <= 0) {
          state.sessionsUntilLongBreak = state.settings.longBreakInterval
        }
      }
    },

    pauseSession: (state) => {
      state.isPaused = true
      state.isActive = false
    },

    resumeSession: (state) => {
      state.isPaused = false
      state.isActive = true
    },

    stopSession: (state, action: PayloadAction<{ interrupted?: boolean }>) => {
      if (state.currentSession) {
        const completedSession: PomodoroSession = {
          ...state.currentSession,
          endTime: new Date().toISOString(),
          completed: !action.payload.interrupted,
          interrupted: action.payload.interrupted || false
        }

        state.sessions.unshift(completedSession)

        // Update stats
        if (completedSession.completed) {
          state.stats.completedSessions += 1
          if (completedSession.type === 'work') {
            state.stats.totalWorkTime += completedSession.duration
            state.stats.todaySessions += 1
            state.stats.weekSessions += 1
            
            if (completedSession.subject) {
              state.stats.subjectTime[completedSession.subject] = 
                (state.stats.subjectTime[completedSession.subject] || 0) + completedSession.duration
            }
          } else {
            state.stats.totalBreakTime += completedSession.duration
          }
        }

        state.stats.totalSessions += 1
        state.stats.averageSessionLength = 
          (state.stats.totalWorkTime + state.stats.totalBreakTime) / state.stats.totalSessions
      }

      state.currentSession = null
      state.isActive = false
      state.isPaused = false
      state.timeRemaining = state.settings.workDuration * 60
    },

    completeSession: (state) => {
      if (state.currentSession) {
        // Mark current session as completed and stop it
        state.currentSession.completed = true
        state.currentSession.endTime = new Date().toISOString()
        state.isActive = false
        state.isPaused = false
        
        // Add to sessions history
        state.sessions.push({ ...state.currentSession })
        
        // Update stats
        state.stats.completedSessions += 1
        if (state.currentSession.type === 'work') {
          state.stats.totalWorkTime += state.currentSession.duration
          if (state.currentSession.subject) {
            state.stats.subjectTime[state.currentSession.subject] = 
              (state.stats.subjectTime[state.currentSession.subject] || 0) + state.currentSession.duration
          }
        } else {
          state.stats.totalBreakTime += state.currentSession.duration
        }
        
        // Clear current session
        state.currentSession = null
        state.timeRemaining = state.settings.workDuration * 60
      }
    },

    updateTimeRemaining: (state, action: PayloadAction<number>) => {
      state.timeRemaining = Math.max(0, action.payload)
      
      if (state.timeRemaining === 0 && state.isActive) {
        pomodoroSlice.caseReducers.completeSession(state)
      }
    },

    updateSettings: (state, action: PayloadAction<Partial<PomodoroSettings>>) => {
      state.settings = { ...state.settings, ...action.payload }
      
      // Update time remaining if not in session
      if (!state.currentSession) {
        state.timeRemaining = state.settings.workDuration * 60
      }
    },

    setSelectedSubject: (state, action: PayloadAction<string>) => {
      state.selectedSubject = action.payload
    },

    setSelectedTopic: (state, action: PayloadAction<string>) => {
      state.selectedTopic = action.payload
    },

    resetCycle: (state) => {
      state.currentCycle = 0
      state.sessionsUntilLongBreak = state.settings.longBreakInterval
    },

    deleteSession: (state, action: PayloadAction<string>) => {
      const sessionIndex = state.sessions.findIndex(s => s.id === action.payload)
      if (sessionIndex !== -1) {
        const session = state.sessions[sessionIndex]
        
        // Update stats
        state.stats.totalSessions -= 1
        if (session.completed) {
          state.stats.completedSessions -= 1
          if (session.type === 'work') {
            state.stats.totalWorkTime -= session.duration
            state.stats.todaySessions = Math.max(0, state.stats.todaySessions - 1)
            state.stats.weekSessions = Math.max(0, state.stats.weekSessions - 1)
            
            if (session.subject) {
              state.stats.subjectTime[session.subject] = 
                Math.max(0, (state.stats.subjectTime[session.subject] || 0) - session.duration)
            }
          } else {
            state.stats.totalBreakTime -= session.duration
          }
        }
        
        state.sessions.splice(sessionIndex, 1)
        
        // Recalculate average
        if (state.stats.totalSessions > 0) {
          state.stats.averageSessionLength = 
            (state.stats.totalWorkTime + state.stats.totalBreakTime) / state.stats.totalSessions
        } else {
          state.stats.averageSessionLength = 0
        }
      }
    },

    startCustomTimer: (state, action: PayloadAction<{ duration: number; title: string }>) => {
      state.customTimer = {
        isActive: true,
        duration: action.payload.duration,
        timeRemaining: action.payload.duration,
        title: action.payload.title
      }
    },

    pauseCustomTimer: (state) => {
      state.customTimer.isActive = false
    },

    resumeCustomTimer: (state) => {
      state.customTimer.isActive = true
    },

    stopCustomTimer: (state) => {
      state.customTimer = {
        isActive: false,
        duration: 30 * 60,
        timeRemaining: 30 * 60,
        title: 'Özel Zamanlayıcı'
      }
    },

    updateCustomTimerTime: (state, action: PayloadAction<number>) => {
      state.customTimer.timeRemaining = Math.max(0, action.payload)
      
      if (state.customTimer.timeRemaining === 0) {
        state.customTimer.isActive = false
      }
    },

    incrementStreak: (state) => {
      state.stats.streakDays += 1
    },

    resetStreak: (state) => {
      state.stats.streakDays = 0
    },

    resetDailyStats: (state) => {
      state.stats.todaySessions = 0
    },

    resetWeeklyStats: (state) => {
      state.stats.weekSessions = 0
    },

    addCustomSession: (state, action: PayloadAction<{ duration: number; subject?: string; topic?: string; title: string }>) => {
      const customSession: PomodoroSession = {
        id: Date.now().toString(),
        type: 'work',
        duration: action.payload.duration,
        subject: action.payload.subject,
        topic: action.payload.topic,
        startTime: new Date().toISOString(),
        endTime: new Date(Date.now() + action.payload.duration * 1000).toISOString(),
        completed: true,
        interrupted: false
      }

      state.sessions.unshift(customSession)
      
      // Update stats
      state.stats.totalSessions += 1
      state.stats.completedSessions += 1
      state.stats.totalWorkTime += action.payload.duration
      state.stats.todaySessions += 1
      state.stats.weekSessions += 1
      
      if (action.payload.subject) {
        state.stats.subjectTime[action.payload.subject] = 
          (state.stats.subjectTime[action.payload.subject] || 0) + action.payload.duration
      }
      
      state.stats.averageSessionLength = 
        (state.stats.totalWorkTime + state.stats.totalBreakTime) / state.stats.totalSessions
    },

    updateSessionSubject: (state, action: PayloadAction<{ sessionId: string; subject: string; topic?: string }>) => {
      const session = state.sessions.find(s => s.id === action.payload.sessionId)
      if (session) {
        // Remove time from old subject
        if (session.subject && session.type === 'work') {
          state.stats.subjectTime[session.subject] = 
            Math.max(0, (state.stats.subjectTime[session.subject] || 0) - session.duration)
        }
        
        // Add time to new subject
        session.subject = action.payload.subject
        session.topic = action.payload.topic
        
        if (session.type === 'work') {
          state.stats.subjectTime[action.payload.subject] = 
            (state.stats.subjectTime[action.payload.subject] || 0) + session.duration
        }
      }
    },

    clearAllSessions: (state) => {
      state.sessions = []
      state.stats = {
        totalSessions: 0,
        completedSessions: 0,
        totalWorkTime: 0,
        totalBreakTime: 0,
        streakDays: 0,
        todaySessions: 0,
        weekSessions: 0,
        averageSessionLength: 0,
        subjectTime: {}
      }
    }
  },
})

export const {
  startSession,
  pauseSession,
  resumeSession,
  stopSession,
  completeSession,
  updateTimeRemaining,
  updateSettings,
  setSelectedSubject,
  setSelectedTopic,
  resetCycle,
  deleteSession,
  startCustomTimer,
  pauseCustomTimer,
  resumeCustomTimer,
  stopCustomTimer,
  updateCustomTimerTime,
  incrementStreak,
  resetStreak,
  resetDailyStats,
  resetWeeklyStats,
  addCustomSession,
  updateSessionSubject,
  clearAllSessions,
} = pomodoroSlice.actions

export default pomodoroSlice.reducer